import { useEffect, useState } from "react";
import type { PerWordResult } from "../core/types";
import { wordColor } from "../core/palette";

const LATIN_TO_HEBREW: Record<string, string> = {
  // Best-effort transliteration for users without a Hebrew keyboard.
  a: "א", b: "ב", g: "ג", d: "ד", h: "ה", v: "ו", w: "ו",
  z: "ז", x: "ח", T: "ט", y: "י", k: "כ", l: "ל", m: "מ",
  n: "נ", s: "ס", "'": "ע", p: "פ", c: "צ", q: "ק", r: "ר",
  S: "ש", t: "ת",
};

function transliterate(input: string): string {
  let out = "";
  for (const ch of input) {
    out += LATIN_TO_HEBREW[ch] ?? ch;
  }
  return out;
}

export interface SearchPanelProps {
  wordsText: string;
  setWordsText: (s: string) => void;
  dMin: number;
  setDMin: (n: number) => void;
  dMax: number;
  setDMax: (n: number) => void;
  backward: boolean;
  setBackward: (b: boolean) => void;
  ready: boolean;
  searching: boolean;
  results: PerWordResult[];
  searchMs: number | null;
  renderMs: number | null;
  mutedWords: ReadonlySet<number>;
  toggleMuteWord: (i: number) => void;
  acceptedWords: string[];
  rejected: Array<{ token: string; reason: "too-short" | "too-long" | "no-hebrew" }>;
}

interface SkipControlProps {
  id: string;
  label: string;
  value: number;
  min: number;
  max: number;
  onChange: (v: number) => void;
}

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

function SkipControl(p: SkipControlProps): JSX.Element {
  const set = (v: number) => p.onChange(clamp(v, p.min, p.max));
  return (
    <div className="skip-control">
      <span className="skip-label">{p.label}</span>
      <button
        className="skip-step"
        onClick={() => set(p.value - 1)}
        aria-label={`decrease ${p.label}`}
      >
        −
      </button>
      <input
        id={p.id}
        type="number"
        min={p.min}
        max={p.max}
        value={p.value}
        onChange={(e) => {
          const n = parseInt(e.target.value, 10);
          if (!Number.isNaN(n)) set(n);
        }}
      />
      <button
        className="skip-step"
        onClick={() => set(p.value + 1)}
        aria-label={`increase ${p.label}`}
      >
        +
      </button>
      <input
        type="range"
        className="skip-slider"
        min={p.min}
        max={p.max}
        value={p.value}
        onChange={(e) => set(parseInt(e.target.value, 10))}
      />
    </div>
  );
}

export function SearchPanel(props: SearchPanelProps): JSX.Element {
  const [showTransliterate, setShowTransliterate] = useState(false);

  useEffect(() => {
    const handler = (ev: KeyboardEvent) => {
      const tag = document.activeElement?.tagName;
      if (ev.key === "/" && tag !== "INPUT" && tag !== "TEXTAREA") {
        ev.preventDefault();
        document.getElementById("words-input")?.focus();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, []);

  const onWordsChange = (raw: string) => {
    props.setWordsText(showTransliterate ? transliterate(raw) : raw);
  };

  const totalHits = props.results.reduce((acc, r) => acc + r.hits.length, 0);
  const anyTruncated = props.results.some((r) => r.truncated);

  const rejectedCounts = props.rejected.reduce(
    (acc, r) => {
      acc[r.reason] += 1;
      return acc;
    },
    { "too-short": 0, "too-long": 0, "no-hebrew": 0 } as Record<
      "too-short" | "too-long" | "no-hebrew",
      number
    >,
  );
  const rejectedTotal = props.rejected.length;
  const [showRejected, setShowRejected] = useState(false);

  return (
    <div className="search-panel">
      <h1>דילוגים · Dilugim</h1>
      <p className="subtitle">
        Equidistant Letter Sequence search over the Torah.
      </p>

      <div className="field">
        <label htmlFor="words-input">Words (one per line)</label>
        <textarea
          id="words-input"
          dir="rtl"
          lang="he"
          rows={6}
          autoFocus
          spellCheck={false}
          autoComplete="off"
          value={props.wordsText}
          onChange={(e) => onWordsChange(e.target.value)}
          placeholder="תורה&#10;משה&#10;ישראל"
          disabled={!props.ready}
        />
        <label className="checkbox-row">
          <input
            type="checkbox"
            checked={showTransliterate}
            onChange={(e) => setShowTransliterate(e.target.checked)}
          />
          Auto-transliterate Latin → Hebrew
        </label>
        {rejectedTotal > 0 && (
          <div className="parse-summary" role="status" aria-live="polite">
            <div className="parse-summary-line">
              <strong>{props.acceptedWords.length}</strong>
              <span> accepted · </span>
              <strong>{rejectedTotal}</strong>
              <span> ignored</span>
              {rejectedCounts["too-long"] > 0 && (
                <span className="parse-tag" title="Tokens longer than 15 letters look like pasted prose, not search words.">
                  {rejectedCounts["too-long"]} too long
                </span>
              )}
              {rejectedCounts["too-short"] > 0 && (
                <span className="parse-tag" title="Single-letter tokens have too many ELS hits to be useful.">
                  {rejectedCounts["too-short"]} too short
                </span>
              )}
              {rejectedCounts["no-hebrew"] > 0 && (
                <span className="parse-tag" title="No Hebrew letters in this token.">
                  {rejectedCounts["no-hebrew"]} non-Hebrew
                </span>
              )}
              <button
                type="button"
                className="parse-summary-toggle"
                onClick={() => setShowRejected((s) => !s)}
              >
                {showRejected ? "hide" : "show"}
              </button>
            </div>
            {showRejected && (
              <ul className="parse-rejected" dir="rtl" lang="he">
                {props.rejected.slice(0, 24).map((r, i) => (
                  <li key={i} title={`ignored: ${r.reason}`}>
                    {r.token.length > 24
                      ? r.token.slice(0, 24) + "…"
                      : r.token || "(empty)"}
                  </li>
                ))}
                {props.rejected.length > 24 && (
                  <li className="parse-rejected-more">
                    +{props.rejected.length - 24}…
                  </li>
                )}
              </ul>
            )}
          </div>
        )}
      </div>

      <div className="field skip-range">
        <label>Skip range</label>
        <SkipControl
          id="d-min"
          label="min"
          value={props.dMin}
          min={1}
          max={Math.max(1, props.dMax)}
          onChange={(v) => {
            const next = Math.min(v, props.dMax);
            props.setDMin(next);
          }}
        />
        <SkipControl
          id="d-max"
          label="max"
          value={props.dMax}
          min={Math.max(1, props.dMin)}
          max={2000}
          onChange={(v) => {
            const next = Math.max(v, props.dMin);
            props.setDMax(next);
          }}
        />
      </div>

      <label className="checkbox-row">
        <input
          type="checkbox"
          checked={props.backward}
          onChange={(e) => props.setBackward(e.target.checked)}
        />
        Also search backward
      </label>

      <div className="status">
        {!props.ready ? (
          <span>Loading index…</span>
        ) : (
          <>
            <div>
              {props.searching ? "searching… · " : ""}
              {totalHits} hit{totalHits === 1 ? "" : "s"} across{" "}
              {props.results.length} word{props.results.length === 1 ? "" : "s"}
              {anyTruncated ? " (some truncated)" : ""}
              {props.searchMs !== null
                ? ` · search ${props.searchMs.toFixed(1)} ms`
                : ""}
              {props.renderMs !== null
                ? ` · render ${props.renderMs.toFixed(1)} ms`
                : ""}
            </div>
            {props.results.length > 0 && (
              <ul className="word-legend">
                {props.results.map((r, i) => {
                  const muted = props.mutedWords.has(i);
                  return (
                    <li
                      key={`${r.canonical}-${i}`}
                      className={muted ? "muted" : undefined}
                      onClick={() => props.toggleMuteWord(i)}
                      title={
                        muted
                          ? "Click to show this word again"
                          : "Click to hide this word from all panes"
                      }
                    >
                      <span
                        className="swatch"
                        style={{
                          backgroundColor: muted ? "transparent" : wordColor(i),
                          borderColor: wordColor(i),
                        }}
                      />
                      <span className="word-label" dir="rtl" lang="he">
                        {r.canonical || "(empty)"}
                      </span>
                      <span className="word-count">
                        {r.hits.length}
                        {r.truncated ? "+" : ""}
                      </span>
                    </li>
                  );
                })}
              </ul>
            )}
          </>
        )}
      </div>
    </div>
  );
}
